import { useState, useEffect, useRef } from "react"
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip
} from "recharts";
import { dbEvents } from "../../../db/dbEvents"
import { getAllOrders, getHistoricOrders } from "../../../Model/Orders"
import { getAllRegisterEntries } from "../../../Model/RegisterEntries";
import { BaseContainer } from "../../../Components/BaseContainer"
import ScrollWrapper from "../../../Components/ScrollWrapper"
import Order from "../../../APIController/Order";
import { saveData } from "../../../db/db_helper"
import Text from "../../../Components/Text"
import ToggleSwitch from "../../../Components/ToggleSwitch"
import Button from "../../../Components/Buttons/Button";
import { useModal } from "../../../Components/Modal";
import { GovermentRapport } from "./GovermentRapport";
import SubscriptionHelper from "../../../Helper/SubscriptionHelper";

export const StatisticDashboard = () => {
    const { getStatistics } = Order();
    const { ModalView, openModal } = useModal()
    const { isAllowed, hasActive, getValue } = SubscriptionHelper()
    const currency = getValue("currency")

    const isFetchingHistoric = useRef(false);
    const selectedRangeRef = useRef("Heute");

    const [today, setToday] = useState([])
    const [past, setPast] = useState([])
    const [all, setAll] = useState([])
    const [selected, setSelected] = useState([])
    const [selectedRange, setSelectedRange] = useState("Heute")
    const [selectedEntries, setSelectedEntries] = useState([])
    const [customRange, setCustomRange] = useState({
        from: null,
        to: null
    })

    // Ref spiegelt immer den aktuellen customRange (kein Stale-Closure-Problem)
    const customRangeRef = useRef({ from: null, to: null });

    async function initTodayEntries() {
        const data = await getAllRegisterEntries()
        setSelectedEntries(data)
    }

    function handleRapportClick() {
        openModal(<GovermentRapport orders={selected} />)
    }

    useEffect(() => {
        const data = [...today, ...past]
            .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
        setAll(data);
    }, [today, past]);

    // Wenn all sich ändert, Range neu anwenden — aber nie "Custom" (der setzt selected direkt)
    useEffect(() => {
        if (selectedRangeRef.current !== "Custom") {
            updateSelectedRange(selectedRangeRef.current);
        }
    }, [all]);

    useEffect(() => {
        handleOrderData();
        handleHistoricData();
        initData();
        initTodayEntries();

        const handleChange = (e) => {
            if (["orders", "sync_orders"].includes(e.detail.store)) {
                handleOrderData();
            }
            // historic_data absichtlich nicht hier — State wird direkt via setPast gemanagt
        };
        dbEvents.addEventListener("dbChange", handleChange);

        return () => {
            dbEvents.removeEventListener("dbChange", handleChange);
        };
    }, []);

    const initData = async () => {
        setSelected(await getAllOrders())
    }

    const updateSelectedRange = async (range) => {
        setSelectedRange(range);
        selectedRangeRef.current = range;

        const now = new Date();

        let start;
        let end = new Date();

        switch (range) {
            case "Heute":
                start = new Date();
                start.setHours(0, 0, 0, 0);
                break;

            case "Woche":
                start = new Date();
                const day = start.getDay() || 7;
                start.setDate(start.getDate() - day + 1);
                start.setHours(0, 0, 0, 0);
                break;

            case "Monat":
                start = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    1
                );
                break;

            case "Custom": {
                // Ref lesen statt State — kein Stale-Closure-Problem
                const { from, to } = customRangeRef.current;
                if (!from || !to) return;

                start = new Date(from);
                end = new Date(to);
                end.setHours(23, 59, 59, 999);

                // Daten für Range holen/ergänzen
                const mergedPast = await fetchOrUpdate(past, start, end);
                if (mergedPast !== past) {
                    setPast(mergedPast);
                }

                // Direkt filtern — nicht auf useEffect([all]) warten
                const allCurrent = [...today, ...mergedPast];
                const filteredCustom = allCurrent.filter(order => {
                    const date = new Date(order.startTime);
                    return date >= start && date <= end;
                });
                setSelected(filteredCustom);
                return;
            }

            default:
                return;
        }

        const filtered = all.filter(order => {
            const date = new Date(order.startTime);
            return date >= start && date <= end;
        });

        setSelected(filtered);
    };

    const handleOrderData = async () => {
        setToday(await getAllOrders())
    }

    const handleHistoricData = async () => {
        if (isFetchingHistoric.current) return;
        isFetchingHistoric.current = true;

        try {
            const ystEnd = new Date();
            ystEnd.setDate(ystEnd.getDate() - 1);
            ystEnd.setHours(23, 59, 59, 999);

            const startRange = new Date(ystEnd);
            startRange.setDate(startRange.getDate() - 31);
            startRange.setHours(0, 0, 0, 0);

            let data = await getHistoricOrders(startRange, ystEnd);
            data = await fetchOrUpdate(data, startRange, ystEnd);

            setPast(data);
        } finally {
            isFetchingHistoric.current = false;
        }
    };

    const fetchOrUpdate = async (data, startRange, endRange) => {
        if (!data || data.length === 0) {
            const fetchedData = await getStatistics(startRange, endRange);
            if (fetchedData && fetchedData.length > 0) {
                await saveData("historic_data", [...fetchedData]);
                return fetchedData;
            }
            return [];
        }

        const sortedData = [...data].sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
        const actualLastEntryDate = new Date(sortedData[0].startTime);
        const actualFirstEntryDate = new Date(sortedData[sortedData.length - 1].startTime);

        const oneDayMs = 24 * 60 * 60 * 1000;
        let updatedData = [...data];
        let hasChanges = false;

        if (endRange - actualLastEntryDate > oneDayMs) {
            const gapStart = new Date(actualLastEntryDate);
            gapStart.setDate(gapStart.getDate() + 1);
            gapStart.setHours(0, 0, 0, 0);

            const fetched = await getStatistics(gapStart, endRange);
            if (fetched && fetched.length > 0) {
                updatedData = [...fetched, ...updatedData];
                hasChanges = true;
            }
        }

        if (actualFirstEntryDate - startRange > oneDayMs) {
            const gapEnd = new Date(actualFirstEntryDate);
            gapEnd.setDate(gapEnd.getDate() - 1);
            gapEnd.setHours(23, 59, 59, 999);

            const fetched = await getStatistics(startRange, gapEnd);
            if (fetched && fetched.length > 0) {
                updatedData = [...updatedData, ...fetched];
                hasChanges = true;
            }
        }

        if (hasChanges) {
            await saveData("historic_data", updatedData);
            return updatedData;
        }

        return data;
    };

    const getRevenuePerWeekday = () => {
        const days = [
            { name: "Mo", revenue: 0 },
            { name: "Di", revenue: 0 },
            { name: "Mi", revenue: 0 },
            { name: "Do", revenue: 0 },
            { name: "Fr", revenue: 0 },
            { name: "Sa", revenue: 0 },
            { name: "So", revenue: 0 },
        ];

        selected
            .filter(o => o.status !== "WASTE" && o.status !== "CANCELED")
            .forEach(order => {
                const date = new Date(order.startTime);
                const weekday = (date.getDay() + 6) % 7;
                days[weekday].revenue += order.totalPrice;
            });

        return days;
    };

    const getRevenuePerPaymentType = () => {
        const types = {
            ON_COUNTER: { name: "Bargeld", revenue: 0 },
            CARD: { name: "Kartenzahlung", revenue: 0 },
            PAYED_INSIDE: { name: "Woanders bezahlt", revenue: 0 }
        };

        selected
            .filter(order => order.status !== "WASTE" && order.status !== "CANCELED")
            .forEach(order => {
                const type = order.paymentType;
                if (types[type]) {
                    types[type].revenue += order.totalPrice || 0;
                }
            });

        return Object.values(types).sort((a, b) => b.revenue - a.revenue);
    };

    const getTopProducts = (list) => {
        const products = {};

        list
            .filter(order => order.status !== "WASTE")
            .forEach(order => {
                order.orderProducts?.forEach(product => {
                    const id = product.productId;
                    if (!products[id]) {
                        products[id] = { id, name: product.name, count: 0 };
                    }
                    products[id].count += product.count || 0;
                });
                order.coupons?.forEach(coupon => {
                    coupon.offerSnapshotProducts?.forEach(product => {
                        const id = product.productId;
                        if (!products[id]) {
                            products[id] = { id, name: product.name, count: 0 };
                        }
                        products[id].count += product.count || 0;
                    });
                });
            });

        return Object.values(products)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    };

    const getWasteProducts = (list) => {
        const products = {};

        list
            .filter(order => order.status === "WASTE")
            .forEach(order => {
                order.orderProducts?.forEach(product => {
                    const id = product.productId;
                    if (!products[id]) {
                        products[id] = { id, name: product.name, count: 0 };
                    }
                    products[id].count += product.count || 0;
                });
            });

        return Object.values(products).sort((a, b) => b.count - a.count);
    };

    const getOrdersPerHour = () => {
        const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));

        selected
            .filter(o => o.status !== "WASTE" && o.status !== "CANCELED")
            .forEach(order => {
                const hour = new Date(order.startTime).getHours();
                hours[hour].count += 1;
            });

        return hours;
    };

    const productListings = (products) => (
        <div className="flex flex-col gap-3 mt-2">
            {(() => {
                const maxCount = products.length ? products[0].count : 0;

                return products.map(product => {
                    const percentage = maxCount ? (product.count / maxCount) * 100 : 0;

                    return (
                        <div key={product.id} className="flex items-center gap-3">
                            <div className="w-32 truncate text-sm">{product.name}</div>
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#C9A24B]"
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                            <div className="w-10 text-right text-sm">{product.count}x</div>
                        </div>
                    );
                });
            })()}
        </div>
    );

    const header = () => (
        <div className="flex w-full h-min md:grid md:grid-cols-4 flex-col gap-3">
            <BaseContainer>
                <div className="flex-col gap-3">
                    <Text type="body">Umsatz</Text>
                    <Text type="main">
                        {selected.filter(o => o.status !== "WASTE" && o.status !== "CANCELED").reduce((sum, order) => sum + order.totalPrice, 0).toFixed(2)} {currency}
                    </Text>
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex-col gap-3">
                    <Text type="body">Bestellungen</Text>
                    <Text type="main">
                        {selected.filter(o => o.status !== "WASTE").length}
                    </Text>
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex-col gap-3">
                    <Text type="body">Ø Bestellwert</Text>
                    <Text type="main">
                        {(() => {
                            const orders = selected.filter(o => o.status !== "WASTE");
                            const avg = orders.length
                                ? orders.reduce((sum, o) => sum + o.totalPrice, 0) / orders.length
                                : 0;
                            return `${avg.toFixed(2)} ${currency}`;
                        })()}
                    </Text>
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex-col gap-3">
                    <Text type="body">Ø Bearbeitungszeit</Text>
                    <Text type="main">
                        {(() => {
                            const orders = selected.filter(o => o.duration);
                            if (!orders.length) return "-";

                            const totalMs = orders.reduce((sum, o) => {
                                const match = o.duration.match(
                                    /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/
                                );
                                if (!match) return sum;

                                const hours = parseFloat(match[1] || 0);
                                const minutes = parseFloat(match[2] || 0);
                                const seconds = parseFloat(match[3] || 0);

                                return sum + ((hours * 3600 + minutes * 60 + seconds) * 1000);
                            }, 0);

                            const avgMs = totalMs / orders.length;
                            const totalSeconds = Math.floor(avgMs / 1000);
                            const minutes = Math.floor(totalSeconds / 60);
                            const seconds = totalSeconds % 60;

                            return `${minutes}m ${seconds}s`;
                        })()}
                    </Text>
                </div>
            </BaseContainer>
        </div>
    );

    const barChart = () => (
        <BaseContainer>
            <Text type="boldbody">Umsatzvergleich Wochentage</Text>
            <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getRevenuePerWeekday()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(v) => `${v} ${currency}`} />
                        <Tooltip formatter={(v) => `${v.toFixed(2)} ${currency}`} />
                        <Bar dataKey="revenue" fill="#C9A24B" radius={[5, 5, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </BaseContainer>
    );

    const sellingInfos = () => (
        <div className="flex w-full h-min md:grid md:grid-cols-2 md:grid-rows-2 flex-col gap-3 pb-5">
            <BaseContainer>
                <div className="flex flex-col gap-3">
                    <Text type="boldbody">Top Produkte</Text>
                    {productListings(getTopProducts(selected))}
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex flex-col gap-3">
                    <Text type="boldbody">Waste Produkte</Text>
                    {productListings(getWasteProducts(selected))}
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex flex-col gap-3">
                    <Text type="boldbody">Umsatz pro Verkaufstyp</Text>
                    <div className="flex flex-col gap-3 mt-2">
                        {(() => {
                            const payments = getRevenuePerPaymentType();
                            const maxRevenue = payments.length ? payments[0].revenue : 0;

                            return payments.map(payment => {
                                const percentage = maxRevenue ? (payment.revenue / maxRevenue) * 100 : 0;

                                return (
                                    <div key={payment.name} className="flex flex-col gap-1">
                                        <div className="flex justify-between text-sm">
                                            <span>{payment.name}</span>
                                            <span>{payment.revenue.toFixed(2)} {currency}</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-[#C9A24B]"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </BaseContainer>
            <BaseContainer>
                <div className="flex flex-col gap-3 h-full">
                    <Text type="boldbody">Stosszeiten</Text>
                    {(() => {
                        const hours = getOrdersPerHour();
                        const activeIndices = hours
                            .map((h, i) => (h.count > 0 ? i : null))
                            .filter(i => i !== null);

                        const start = activeIndices.length ? Math.max(0, Math.min(...activeIndices) - 1) : 8;
                        const end = activeIndices.length ? Math.min(23, Math.max(...activeIndices) + 1) : 22;

                        const visible = hours.slice(start, end + 1);
                        const maxCount = Math.max(...visible.map(h => h.count), 1);

                        return (
                            <>
                                <div className="flex items-end gap-1 h-20 mt-2">
                                    {visible.map(h => {
                                        const pct = (h.count / maxCount) * 100;
                                        const color = pct > 70 ? "#C9A24B" : pct > 35 ? "#EEDFAF" : "#F1EFE8";

                                        return (
                                            <div
                                                key={h.hour}
                                                className="flex-1 rounded-t"
                                                title={`${h.hour}:00 – ${h.count} Bestellungen`}
                                                style={{ height: `${Math.max(pct, 4)}%`, background: color }}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="flex justify-between text-xs text-gray-400 mt-1">
                                    {visible
                                        .filter((_, i) => i % 2 === 0)
                                        .map(h => <span key={h.hour}>{h.hour}</span>)}
                                </div>
                            </>
                        );
                    })()}
                </div>
            </BaseContainer>
            {(isAllowed("allowCashierUserTracking") && hasActive("cashierUserTracking")) && (
                <BaseContainer>
                    <div className="flex flex-col gap-3 h-full">
                        <Text type="boldbody">Kassendifferenzen heute</Text>
                        {selectedEntries
                            .filter(e => e.startAmount !== null && e.finalAmount !== null && e.startAmount !== e.finalAmount)
                            .map((entry) => (
                                <div key={entry.id} className="w-full flex flex-col">
                                    <div>{entry.user.firstName} {entry.user.lastName}</div>
                                    <div className="flex justify-between">
                                        <div>{new Date(entry.startTime).toLocaleDateString("de-CH")}</div>
                                        <div className={(entry.startAmount - entry.finalAmount).toFixed(2) > 0 ? "text-green-500" : "text-red-500"}>
                                            {(entry.startAmount - entry.finalAmount).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </BaseContainer>
            )}
        </div>
    );

    return (
        <div className="bg-gray-100 w-full h-full flex flex-col">
            <div className="pt-3 pl-3 pr-3 pb-3 flex flex-col gap-2">
                <ToggleSwitch
                    toggleObjects={[
                        { text: "Heute", selectedStyle: "bg-[#142235] text-[#C9A24B]", unselectedStyle: "bg-white text-[#C7C2B7]", onClickAction: () => updateSelectedRange("Heute") },
                        { text: "Woche", selectedStyle: "bg-[#142235] text-[#C9A24B]", unselectedStyle: "bg-white text-[#C7C2B7]", onClickAction: () => updateSelectedRange("Woche") },
                        { text: "Monat", selectedStyle: "bg-[#142235] text-[#C9A24B]", unselectedStyle: "bg-white text-[#C7C2B7]", onClickAction: () => updateSelectedRange("Monat") },
                        { text: "Custom", selectedStyle: "bg-[#142235] text-[#C9A24B]", unselectedStyle: "bg-white text-[#C7C2B7]", onClickAction: () => updateSelectedRange("Custom") },
                    ]}
                    buttonClasses="w-fit px-5 rounded-xl border border-white"
                />
                {selectedRange === "Custom" && (
                    <div className="flex gap-3 mt-3">
                        <input
                            className="rounded-lg px-2"
                            type="date"
                            onChange={(e) => {
                                const updated = { ...customRangeRef.current, from: e.target.value };
                                customRangeRef.current = updated;
                                setCustomRange(updated);
                            }}
                        />
                        <input
                            className="rounded-lg px-2"
                            type="date"
                            onChange={(e) => {
                                const updated = { ...customRangeRef.current, to: e.target.value };
                                customRangeRef.current = updated;
                                setCustomRange(updated);
                            }}
                        />
                        <Button
                            text={"Anwenden"}
                            borderStyle="rounded-xl px-2"
                            classNames="bg-[#C9A24B] text-[#142235]"
                            onClickAction={() => updateSelectedRange("Custom")}
                        />
                    </div>
                )}
                <Button tIconName={"file-description"} text={"Abrechnungsrapport erstellen"} onClickAction={() => handleRapportClick()} />
            </div>
            <ScrollWrapper>
                <div className="flex flex-col gap-3 h-full w-full pt-3 pl-3 pr-3">
                    {header()}
                    {barChart()}
                    {sellingInfos()}
                </div>
            </ScrollWrapper>
            <ModalView classNames="w-auto h-full" />
        </div>
    );
};